class TabList extends HTMLUListElement {
  constructor() {
    super();

    this.controls.forEach((button) => button.addEventListener('click', this.handleButtonClick.bind(this)));
  }

  get controls() {
    return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
  }

  handleButtonClick(event) {
    event.preventDefault();

    this.controls.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');

      const panel = document.getElementById(button.getAttribute('aria-controls'));
      panel?.removeAttribute('open');
    });

    const target = event.currentTarget;
    target.setAttribute('aria-expanded', 'true');

    const panel = document.getElementById(target.getAttribute('aria-controls'));
    panel?.setAttribute('open', '');
  }

  reset() {
    const firstControl = this.controls[0];
    firstControl.dispatchEvent(new Event('click'));
  }
}
customElements.define('tab-list', TabList, { extends: 'ul' });

class CartDrawer extends DrawerElement {
  constructor() {
    super();

    this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get shouldAppendToBody() {
    return false;
  }

  get recentlyViewed() {
    return this.querySelector('recently-viewed');
  }

  get tabList() {
    return this.querySelector('[is="tab-list"]');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
    if (this.recentlyViewed) {
      this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('cart:bundled-sections', this.onPrepareBundledSectionsListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  onPrepareBundledSections(event) {
    event.detail.sections.push(this.sectionId);
  }

  onRecentlyViewedEmpty() {
    this.recentlyViewed.innerHTML = `
    <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
      <div class="drawer__empty grid gap-5 md:gap-8">
        <h2 class="drawer__empty-text heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</h2>
      </div>
    </div>
    `;
  }

  async onCartRefresh(event) {
    const id = `MiniCart-${this.sectionId}`;
    if (document.getElementById(id) === null) return;

    const responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`)).text();
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id).innerHTML;

    if (event.detail.open === true) {
      this.show();
    }
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.tabList) {
      this.tabList.reset();

      if (this.open) {
        theme.a11y.trapFocus(this, this.focusElement);
      }
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartRemoveButton extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.getAttribute('data-index'), 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton, { extends: 'a' });

class CartItems extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

    const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
    if (miniCart) {
      const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
      if (updatedElement) {
        miniCart.innerHTML = updatedElement.innerHTML;
      }
    }

    const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
      }
      else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
    }
    else if (event.cart.item_count === 0) {
      miniCart
        ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
        : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
    }
    else {
      miniCart
        ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
        : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        cart: event.cart
      }
    }));
  }

  onCartError(errors, target) {
    if (target) {
      // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
      this.disableLoading(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    }
    else {
      window.location.href = theme.routes.cart_url;
    }

    alert(errors);
  }

  updateQuantity(line, quantity, name, target) {
    this.enableLoading(line);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const body = JSON.stringify({
      id: line,
      quantity,
      sections: sectionsToBundle
    });

    fetch(`${theme.routes.cart_change_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
        }
      });
  }

  enableLoading(line) {
    const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
    if (loader) loader.hidden = false;
  }

  disableLoading(line) {
    const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
    if (loader) loader.hidden = true;
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
    }
    else if (inputValue > parseInt(target.max)) {
      message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
    }
    else if (inputValue % parseInt(target.step) !== 0) {
      message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    }
    else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }
}
customElements.define('cart-items', CartItems);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  onPrepareBundledSections(event) {
    event.detail.sections.push(this.sectionId);
  }
}
customElements.define('main-cart', MainCart);

class CountryProvince extends HTMLElement {
  constructor() {
    super();

    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
      this.countryElement.dispatchEvent(new Event('change'));
    }
    else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
    this.provinceElement.parentElement.hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.getAttribute('data-province');
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvince);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.onSubmitHandler = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.onSubmitHandler);
  }

  disconnectedCallback() {
    this.submitButton.removeEventListener('click', this.onSubmitHandler);
  }

  onSubmit(event) {
    event.preventDefault();

    this.abortController?.abort();
    this.abortController = new AbortController();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      shipping_address: { zip, country, province }
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        }
        else {
          this.formatError(parsedState);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
        }
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.removeAttribute('aria-busy');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error grid gap-2 text-sm leading-tight">
        <p>${theme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--${shippingRates.length === 0 ? 'error' : 'success'} grid gap-2 text-sm leading-tight">
        <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
        ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
      </div>
    `;

  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });




/* =========================================================
   FREE GIFT — ALLOW ONLY 1 ITEM (BULLETPROOF)
   Page: page-free-gift
========================================================= */

document.addEventListener('DOMContentLoaded', function () {

  if (!document.body.classList.contains('page-free-gift')) return;

  console.log("FREE GIFT LOGIC ACTIVE");

  const selector = 'button.button--primary[data-variant-id]';
  let addToCartLocked = false;
  let cartCleared = false;

  /* ---------- HELPERS ---------- */
  function getButtons() {
    return Array.from(document.querySelectorAll(selector));
  }

  function lockButtons(text = 'Checking Free Gift…') {
    getButtons().forEach(btn => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
      btn.style.cursor = 'not-allowed';

      const txt = btn.querySelector('.btn-text .block');
      if (txt) {
        if (!btn.dataset.originalText) {
          btn.dataset.originalText = txt.textContent.trim();
        }
        txt.textContent = text;
      }
    });
  }

  function disableButtons() {
    lockButtons('Only 1 Free Gift Allowed');
  }

  function enableButtons() {
    if (addToCartLocked) return;

    getButtons().forEach(btn => {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.style.pointerEvents = 'auto';
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';

      const txt = btn.querySelector('.btn-text .block');
      if (txt && btn.dataset.originalText) {
        txt.textContent = btn.dataset.originalText;
      }
    });
  }

  /* ---------- INITIAL HARD LOCK ---------- */
  lockButtons();

  /* ---------- CART CHECK ---------- */
  async function checkCart() {
    const cart = await (await fetch('/cart.js')).json();
    if (cart.items.length >= 1) disableButtons();
    else enableButtons();
  }

  checkCart();

  /* ---------- ADD TO CART HARD BLOCK ---------- */
  document.addEventListener('submit', async function (e) {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;

    if (addToCartLocked) {
      e.preventDefault();
      return;
    }

    addToCartLocked = true;
    lockButtons();

    const cart = await (await fetch('/cart.js')).json();
    if (cart.items.length >= 1) {
      e.preventDefault();
      disableButtons();
      alert('Only 1 Free Gift Allowed');
    }
  });

  /* ---------- CART UPDATED ---------- */
  document.addEventListener('cart:updated', function (e) {
    const cart = e.detail?.cart;
    if (!cart) return;

    if (cart.item_count >= 1) disableButtons();
    else enableButtons();
  });

  /* ---------- DISABLE PRODUCT LINKS ---------- */
  document.querySelectorAll('a[href*="/products/"]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
    });
    link.style.pointerEvents = 'none';
    link.style.cursor = 'not-allowed';
  });

  /* ---------- HIDE QUANTITY FOR FREE GIFT ---------- */
  document.addEventListener('cart:updated', function () {
    document.querySelectorAll('.cart-item, .horizontal-product').forEach(item => {
      const badge = Array.from(item.querySelectorAll('*'))
        .find(el => el.textContent.trim().toLowerCase() === 'freegift');

      if (!badge) return;

      item.querySelectorAll(
        '.quantity, .cart-item__quantity, .quantity__button, .quantity__input'
      ).forEach(el => el.style.display = 'none');
    });
  });

  /* ---------- CLEAR CART LOGIC ---------- */
  async function clearCart(refresh = true) {
    if (cartCleared) return;
    cartCleared = true;

    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    if (refresh) setTimeout(() => location.reload(), 300);
  }

  (async () => {
    const cart = await (await fetch('/cart.js')).json();
    if (cart.items.length > 0) {
      await clearCart(true);
    }
  })();

  window.addEventListener('beforeunload', () => clearCart(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') clearCart(false);
  });

  /* ---------- REMOVE RECOMMENDATIONS ---------- */
  function removeRecommendations() {
    document
      .querySelectorAll('product-recommendations.complementary-products')
      .forEach(el => el.remove());
  }

  removeRecommendations();
  document.addEventListener('cart:updated', removeRecommendations);

  new MutationObserver(removeRecommendations)
    .observe(document.body, { childList: true, subtree: true });

});


/* =========================================================
   FREE GIFT PAGE — LOGO CLICK STAY ON SAME PAGE
========================================================= */

document.addEventListener("DOMContentLoaded", function () {

  if (!document.body.classList.contains("page-free-gift")) return;

  console.log("Free gift logo click locked");

  // Common Shopify header logo selectors
  const logoSelectors = [
    'a[href="/"]',
    'a.site-header__logo-link',
    'a.header__heading-link',
    'a.header__logo',
    'header a'
  ];

  const logoLinks = document.querySelectorAll(logoSelectors.join(','));

  logoLinks.forEach(link => {
    link.addEventListener("click", function (e) {

      // Sirf logo / homepage links block karo
      const href = link.getAttribute("href");
      if (!href || href === "/" || href.includes("portabites.com")) {
        e.preventDefault();
        e.stopPropagation();

        // Same page reload (optional)
        window.location.href = window.location.href;
      }
    });
  });

});


/* =========================================================
   FREE GIFT PAGE — DISABLE ALL FOOTER LINKS
========================================================= */

document.addEventListener("DOMContentLoaded", function () {

  if (!document.body.classList.contains("page-free-gift")) return;

  console.log("Footer links disabled on free gift page");

  // Common footer selectors (Shopify themes)
  const footerSelectors = [
    'footer a',
    '.footer a',
    '.site-footer a'
  ];

  document
    .querySelectorAll(footerSelectors.join(','))
    .forEach(link => {

      // Click completely block
      link.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
      });

      // Visual feedback
      link.style.pointerEvents = 'none';
      link.style.cursor = 'not-allowed';
      link.style.opacity = '0.6';
    });

});



/* =========================================================
   FREE GIFT — CART: ONLY REMOVE SHOULD WORK
========================================================= */

function lockFreeGiftCartLinks() {

  document.querySelectorAll('.cart-item, .horizontal-product').forEach(item => {

    // FREE GIFT detect
    const isFreeGift = Array.from(item.querySelectorAll('*'))
      .some(el => el.textContent.trim().toLowerCase() === 'freegift');

    if (!isFreeGift) return;

    console.log('Locking free gift cart links (except remove)');

    // Sab links disable karo
    item.querySelectorAll('a').forEach(link => {

      const text = link.textContent.trim().toLowerCase();

      // ✅ REMOVE ko allow karo
      if (
        text === 'remove' ||
        link.classList.contains('cart-remove') ||
        link.classList.contains('remove') ||
        link.getAttribute('aria-label')?.toLowerCase().includes('remove')
      ) {
        return; // skip → remove works
      }

      // ❌ Baaki sab disable
      link.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
      });

      link.style.pointerEvents = 'none';
      link.style.cursor = 'default';
      link.setAttribute('aria-disabled', 'true');
    });
  });
}

/* ---- EVENTS ---- */
document.addEventListener('DOMContentLoaded', lockFreeGiftCartLinks);
document.addEventListener('cart:updated', lockFreeGiftCartLinks);
document.addEventListener('cart:change', lockFreeGiftCartLinks);

// AJAX delay safety
setTimeout(lockFreeGiftCartLinks, 500);
setTimeout(lockFreeGiftCartLinks, 1200);



/* =========================================================
   FREE GIFT — AUTO REFRESH PAGE AFTER REMOVE
========================================================= */

document.addEventListener('click', function (e) {

  if (!document.body.classList.contains('page-free-gift')) return;

  const removeBtn = e.target.closest(
    'a, button'
  );

  if (!removeBtn) return;

  const text = removeBtn.textContent.trim().toLowerCase();
  const aria = removeBtn.getAttribute('aria-label')?.toLowerCase() || '';

  // ✅ Detect REMOVE click
  if (
    text === 'remove' ||
    aria.includes('remove') ||
    removeBtn.classList.contains('remove') ||
    removeBtn.classList.contains('cart-remove')
  ) {
    console.log('Free gift removed → refreshing page');

    // ⏳ Shopify AJAX remove ke baad refresh
    setTimeout(() => {
      window.location.reload();
    }, 700);
  }

});



/* =========================================================
   FREE GIFT PAGE — HIDE RECENTLY VIEWED & EMPTY CART LINKS
========================================================= */

function hideFreeGiftCartUI() {

  if (!document.body.classList.contains('page-free-gift')) return;

  /* ---- 1. Hide Recently Viewed TAB button ---- */
  document.querySelectorAll('.drawer__tabs li').forEach(li => {
    if (li.textContent.trim().toLowerCase().includes('recently viewed')) {
      li.remove();
    }
  });

  /* ---- 2. Remove Recently Viewed PANEL ---- */
  document
    .querySelectorAll('#RecentlyViewed-sections--19356514812015__cart-drawer, recently-viewed')
    .forEach(el => el.remove());

  /* ---- 3. Remove empty cart collection links ---- */
  document
    .querySelectorAll('.drawer__empty-collections, .drawer__empty-collections a')
    .forEach(el => el.remove());
}

/* ---- EVENTS (AJAX SAFE) ---- */
document.addEventListener('DOMContentLoaded', hideFreeGiftCartUI);
document.addEventListener('cart:updated', hideFreeGiftCartUI);
document.addEventListener('cart:change', hideFreeGiftCartUI);
document.addEventListener('cart:refresh', hideFreeGiftCartUI);

/* ---- Safety for delayed render ---- */
setTimeout(hideFreeGiftCartUI, 500);
setTimeout(hideFreeGiftCartUI, 1200);






